import React, { useEffect, useRef } from 'react';
import { Star } from 'lucide-react';

const useScrollAnimation = (threshold = 0.08) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll('.fade-up, .fade-left, .fade-right, .scale-in').forEach((child) => {
            child.classList.add('visible');
          });
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
};

const testimonials = [
  {
    quote: 'Excelente trabajo, cumplieron en tiempo y forma. La calidad de los acabados superó nuestras expectativas. Muy recomendados.',
    author: 'Familia Ramírez Cruz',
    location: 'Oaxaca de Juárez',
    project: 'Casa residencial, 2023',
    initials: 'RC',
  },
  {
    quote: 'Desde el primer día nos explicaron todo con claridad. El seguimiento fue constante y el resultado es exactamente lo que soñamos para nuestra familia.',
    author: 'Ing. Carlos Mendoza',
    location: 'Santa María del Tule',
    project: 'Residencia de dos niveles, 2022',
    initials: 'CM',
  },
  {
    quote: 'Lo que más valoramos fue la transparencia en el presupuesto. Sin sorpresas, sin cobros extra. Una empresa seria y profesional.',
    author: 'Dra. Laura Bernal',
    location: 'Xoxocotlán, Oaxaca',
    project: 'Casa contemporánea, 2023',
    initials: 'LB',
  },
];

const StarRow = () => (
  <div style={{ display: 'flex', gap: '0.2rem', marginBottom: '1rem' }}>
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={14} fill="#f59e0b" color="#f59e0b" />
    ))}
  </div>
);

const Testimonials = () => {
  const ref = useScrollAnimation();

  return (
    <section
      ref={ref}
      style={{
        padding: '8rem 2rem',
        background: 'var(--bg-secondary)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top accent */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'var(--red)' }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1200, margin: '0 auto' }}>

        {/* Header */}
        <div className="fade-up" style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <div className="section-label">Lo que dicen nuestros clientes</div>
          <h2 style={{
            fontFamily: 'var(--font-heading)', fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)', color: 'var(--text-primary)', marginTop: '0.5rem',
          }}>
            Clientes que <span style={{ color: 'var(--red)' }}>confían en nosotros</span>
          </h2>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: '1rem', color: 'var(--gray)',
            maxWidth: 480, margin: '1rem auto 0', lineHeight: 1.7,
          }}>
            Más de 200 familias y empresas han confiado en Grupo Constructor Yescas.
          </p>
        </div>

        {/* Testimonials grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1.75rem',
        }} className="testimonials-grid">
          {testimonials.map((t, i) => (
            <div
              key={i}
              className={`fade-up delay-${i + 1}`}
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                borderRadius: 20,
                padding: '2rem',
                boxShadow: '0 4px 24px rgba(0,0,0,0.05)',
                display: 'flex',
                flexDirection: 'column',
                gap: '1.25rem',
                position: 'relative',
              }}
            >
              {/* Quote mark */}
              <div style={{
                position: 'absolute',
                top: '1.25rem',
                right: '1.5rem',
                fontFamily: 'Georgia, serif',
                fontSize: '5rem',
                color: 'var(--red)',
                opacity: 0.08,
                lineHeight: 1,
                userSelect: 'none',
                pointerEvents: 'none',
              }}>"</div>

              <StarRow />

              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.925rem',
                color: 'var(--gray-dark)',
                lineHeight: 1.8,
                fontStyle: 'italic',
                flex: 1,
              }}>
                "{t.quote}"
              </p>

              {/* Author row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', borderTop: '1px solid var(--border-color)', paddingTop: '1.25rem' }}>
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: 'var(--red)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <span style={{
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 800,
                    fontSize: '0.8rem',
                    color: 'white',
                    letterSpacing: '0.05em',
                  }}>{t.initials}</span>
                </div>
                <div>
                  <div style={{
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    color: 'var(--text-primary)',
                    lineHeight: 1.2,
                  }}>{t.author}</div>
                  <div style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.75rem',
                    color: 'var(--gray)',
                    marginTop: 3,
                  }}>{t.project} · {t.location}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .testimonials-grid { grid-template-columns: 1fr !important; max-width: 540px; margin: 0 auto; }
        }
        @media (max-width: 640px) {
          .testimonials-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
};

export default Testimonials;
